// TemplateAuthoringPanel — Plan B3 Task 2 (cogo.template.v1 authoring lane).
//
// The counterpart to `TemplateContentPanel` (which edits an ALREADY
// instantiated template's content) — this panel turns the CURRENTLY open
// studio project (which must be an ORIGINAL, non-instance project — the
// advanced Work-details mount is gated on `!instantiatedFrom &&
// blocks.length >= 1`) into a downloadable `cogo.template.v1` package:
//
//   [슬롯 스캔] -> proposeSlots() on the serialized project bundle -> a
//   review list with per-slot 확인/제외 toggles (default 확인) and a maxLength
//   edit for `text` slots -> templateId/kind/version inputs (client-side
//   validated) -> [패키지 내보내기] -> buildAuthoringExport() -> 3 file
//   downloads.
//
// Registration (committing the two files under `registry/` + wiring
// `registry.ts`) stays entirely REPO-gated (Ajv + the drift-lock gate in
// `template-registry.gate.test.ts`) — this panel never writes to the
// registry itself. The static procedure note at the bottom says so
// explicitly; see `PROCEDURE_NOTE` below.
//
// Panel state is local (useState) — nothing here is persisted to the store,
// mirroring `TemplateContentPanel`'s own dirty-buffer-until-explicit-action
// discipline (here: nothing survives an unmount at all, since there is no
// server-side draft for an unregistered template).

import { useMemo, useState } from 'react'
import type { AppBundle, SlotDecl, TemplatePackage, TypedFailure } from '@cogo/template'
import { proposeSlots } from '@cogo/template'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import { serializeProjectToBundle, type SerializedBundle, type SerializeProjectToBundleBlockInput } from './serialize-project-to-bundle'
import { buildAuthoringExport } from './build-export'
import { downloadJsonFile } from './download-json-file'
import { getTemplateAuthoringCopy } from './template-authoring-copy'

const TEMPLATE_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/
const KIND_OPTIONS: TemplatePackage['kind'][] = ['storefront', 'dashboard', 'document', 'generic']
// B3 Task-2 review fix (2026-07-25) — shown BEFORE the user invests time in
// slot review whenever neither design source (LoadedApp.designSystem raw
// file NOR LoadedApp.tokens server-persisted truth) is present, so the
// export will necessarily land as designRebinding:'pinned'.
function isTextLikeSlot(slot: SlotDecl): boolean {
  return slot.type === 'text' || slot.type === 'richText'
}

export function TemplateAuthoringPanel() {
  const { locale } = useStudioLocale()
  const copy = getTemplateAuthoringCopy(locale)
  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)

  const [slots, setSlots] = useState<SlotDecl[] | null>(null)
  const [excludedSlotIds, setExcludedSlotIds] = useState<Set<string>>(new Set())
  const [maxLengthEdits, setMaxLengthEdits] = useState<Record<string, number>>({})
  const [templateId, setTemplateId] = useState('')
  const [version, setVersion] = useState('1.0.0')
  const [kind, setKind] = useState<TemplatePackage['kind']>('generic')
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading'>('idle')
  const [exportFailures, setExportFailures] = useState<TypedFailure[]>([])

  // See serialize-project-to-bundle.ts's own header comment ("two-source
  // rule", B3 Task-2 review fix 2026-07-25): `tokens` is fed the RAW
  // design-system.json source (`LoadedApp.designSystem`) when present —
  // byte-faithful, preferred — and `derivedTokens` is fed `LoadedApp.tokens`
  // (the server-persisted design truth) as the fallback source for server
  // projects, which NEVER populate `designSystem` at boot (`toLoadedApp` in
  // `project-api.ts` only ever sets `tokens: row.tokens`). Without this
  // fallback, `bundle.files['design-system.json']` was silently absent for
  // every server project, and `buildAuthoringExport`'s (formerly hardcoded)
  // `designRebinding: 'allowed'` made `maskBundle` throw — export failed
  // 100% of the time on real server projects. `useEditorStore`'s canvas-
  // slice does not retain `designSystem` at all after `loadApp` —
  // `useStudioStore().currentProject` is the only live surface that still
  // carries either source.
  const bundle: SerializedBundle | null = useMemo(() => {
    if (!currentProject) return null
    const blocks = (currentProject.blocks ?? {}) as Record<string, SerializeProjectToBundleBlockInput>
    return serializeProjectToBundle({
      manifest: (currentProject.manifest ?? {}) as unknown as Record<string, unknown>,
      tokens: (currentProject.designSystem ?? null) as Record<string, unknown> | null,
      derivedTokens: (currentProject.tokens ?? null) as Record<string, unknown> | null,
      blocks,
      appDataFiles: (currentProject.data ?? {}) as Record<string, unknown>,
    })
  }, [currentProject])

  // Single source of truth for whether ANY design source made it into the
  // bundle — reads the bundle's own emitted files rather than re-deriving
  // presence from `currentProject` a second time (keeps this in exact sync
  // with serializeProjectToBundle's own two-source emission rule above).
  const hasDesignSource = bundle ? bundle.files['design-system.json'] !== undefined : false
  const designRebinding: 'allowed' | 'pinned' = hasDesignSource ? 'allowed' : 'pinned'

  const templateIdValid = TEMPLATE_ID_RE.test(templateId)
  const versionValid = version.trim().length > 0

  function handleScan() {
    if (!bundle) return
    const proposed = proposeSlots(bundle as unknown as AppBundle)
    setSlots(proposed)
    setExcludedSlotIds(new Set())
    setMaxLengthEdits({})
    setExportFailures([])
  }

  function toggleSlot(slotId: string) {
    setExcludedSlotIds((prev) => {
      const next = new Set(prev)
      if (next.has(slotId)) next.delete(slotId)
      else next.add(slotId)
      return next
    })
  }

  function setMaxLength(slotId: string, value: number) {
    setMaxLengthEdits((prev) => ({ ...prev, [slotId]: value }))
  }

  async function handleExport() {
    if (!bundle || !slots) return
    if (!templateIdValid || !versionValid) return

    const confirmedSlots: SlotDecl[] = slots
      .filter((slot) => !excludedSlotIds.has(slot.slotId))
      .map((slot) => {
        const editedMaxLength = maxLengthEdits[slot.slotId]
        if (editedMaxLength === undefined) return slot
        return { ...slot, constraint: { ...slot.constraint, maxLength: editedMaxLength } }
      })

    setExportStatus('loading')
    setExportFailures([])
    try {
      const result = await buildAuthoringExport({
        bundle,
        confirmedSlots,
        templateId,
        version,
        kind,
        projectRef: currentProjectId ?? 'unsaved-studio-project',
        designRebinding,
      })
      if (!result.ok) {
        setExportFailures(result.failures)
        return
      }
      downloadJsonFile(`${templateId}.template.json`, result.export.templateJson)
      downloadJsonFile(`${templateId}.files.json`, result.export.filesJson)
      downloadJsonFile(`${templateId}.bundle.json`, result.export.bundleJson)
    } finally {
      setExportStatus('idle')
    }
  }

  if (!currentProject) {
    return (
      <div className="flex-1 p-3 text-xs text-muted-foreground" data-testid="template-authoring-panel">
        {copy.noProject}
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="template-authoring-panel">
      {!hasDesignSource && (
        <div
          className="border-b border-border bg-muted/40 p-3 text-[11px] text-muted-foreground"
          data-testid="authoring-design-pinned-warning"
        >
          {copy.designPinned}
        </div>
      )}
      <div className="border-b border-border p-3">
        <button
          type="button"
          data-testid="authoring-scan-button"
          className="rounded border px-3 py-1 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleScan}
          disabled={!bundle}
        >
          {copy.scan}
        </button>
      </div>

      {slots && (
        <div className="space-y-2 border-b border-border p-3" data-testid="authoring-slot-list">
          {slots.length === 0 && (
            <div className="text-xs text-muted-foreground" data-testid="authoring-slot-list-empty">
              {copy.noSlots}
            </div>
          )}
          {slots.map((slot) => {
            const excluded = excludedSlotIds.has(slot.slotId)
            return (
              <div
                key={slot.slotId}
                className="flex items-center gap-2 text-sm"
                data-testid={`authoring-slot-${slot.slotId}`}
              >
                <button
                  type="button"
                  data-testid={`authoring-slot-toggle-${slot.slotId}`}
                  aria-pressed={!excluded}
                  className={
                    excluded
                      ? 'rounded border px-2 py-0.5 text-xs text-muted-foreground'
                      : 'rounded border border-primary bg-accent px-2 py-0.5 text-xs text-accent-foreground'
                  }
                  onClick={() => toggleSlot(slot.slotId)}
                >
                  {excluded ? copy.excluded : copy.included}
                </button>
                <span className="font-mono text-xs">{slot.slotId}</span>
                <span className="text-xs text-muted-foreground">{slot.type}</span>
                {isTextLikeSlot(slot) && (
                  <label className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                    maxLength
                    <input
                      type="number"
                      data-testid={`authoring-slot-maxlength-${slot.slotId}`}
                      className="w-16 rounded border bg-background px-1 py-0.5"
                      value={maxLengthEdits[slot.slotId] ?? slot.constraint?.maxLength ?? ''}
                      onChange={(e) => setMaxLength(slot.slotId, Number(e.target.value))}
                    />
                  </label>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="space-y-2 border-b border-border p-3">
        <label className="block text-xs font-medium">
          templateId
          <input
            type="text"
            data-testid="authoring-template-id-input"
            className="mt-1 w-full rounded border bg-background p-2 text-sm"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="my-storefront"
          />
        </label>
        {templateId.length > 0 && !templateIdValid && (
          <div className="text-[11px] text-destructive" data-testid="authoring-template-id-error">
            {copy.invalidTemplateId}
          </div>
        )}
        <label className="block text-xs font-medium">
          version
          <input
            type="text"
            data-testid="authoring-version-input"
            className="mt-1 w-full rounded border bg-background p-2 text-sm"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.0.0"
          />
        </label>
        <label className="block text-xs font-medium">
          kind
          <select
            data-testid="authoring-kind-select"
            className="mt-1 w-full rounded border bg-background p-2 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as TemplatePackage['kind'])}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="space-y-2 p-3">
        <button
          type="button"
          data-testid="authoring-export-button"
          className="w-full rounded border px-3 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleExport}
          disabled={!slots || slots.length === 0 || !templateIdValid || !versionValid || exportStatus === 'loading'}
        >
          {exportStatus === 'loading' ? copy.exporting : copy.exportPackage}
        </button>
        {exportFailures.length > 0 && (
          <div className="space-y-1" data-testid="authoring-export-error">
            {exportFailures.map((failure, i) => (
              <div key={i} className="text-[11px] text-destructive">
                {failure.code}: {failure.message}
              </div>
            ))}
          </div>
        )}
        <div className="text-[11px] text-muted-foreground" data-testid="authoring-procedure-note">
          {copy.procedure}
        </div>
      </div>
    </div>
  )
}
